import { Avatar, Tag, Tooltip } from 'antd';
import { Edit3, Layers3 } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { applicationStore } from '@/stores';
import type { AppCatalogAppItem } from '@/types';
import { formatProtoTimestamp, getStatusMeta } from '../constants';

interface ApplicationTableProps {
  onEdit: (app: AppCatalogAppItem) => void;
}

function AppIdentity({ app }: { app: AppCatalogAppItem }) {
  const displayName =
    app.displayName?.trim() ||
    app.fallbackDisplayName?.trim() ||
    app.displayNameKey?.trim() ||
    app.appId?.trim() ||
    '未命名应用';
  const initial = displayName.slice(0, 1).toUpperCase() || 'A';

  return (
    <div className="flex min-w-[220px] items-center gap-3">
      <Avatar
        src={app.iconUrl || undefined}
        shape="square"
        size={36}
        className="flex-shrink-0 bg-gray-100 text-foreground"
      >
        {initial}
      </Avatar>
      <div className="min-w-0">
        <div className="truncate font-medium text-foreground">
          {displayName}
        </div>
        <div className="truncate font-mono text-xs text-muted-foreground">
          {app.appId}
        </div>
      </div>
    </div>
  );
}

export const ApplicationTable = observer(
  ({ onEdit }: ApplicationTableProps) => {
    const columns: Column<AppCatalogAppItem>[] = [
      {
        id: 'displayName',
        header: '应用',
        width: 260,
        accessor: (app) => <AppIdentity app={app} />,
      },
      {
        id: 'status',
        header: '状态',
        width: 110,
        accessor: (app) => {
          const meta = getStatusMeta(app.status);
          return <Tag color={meta.color}>{meta.label}</Tag>;
        },
      },
      {
        id: 'latestVersionId',
        header: 'Latest',
        width: 150,
        accessor: (app) =>
          app.latestVersionId ? (
            <span className="font-mono text-xs">{app.latestVersionId}</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        id: 'categories',
        header: '分类',
        width: 220,
        accessor: (app) =>
          app.categories?.length ? (
            <div className="flex max-w-[220px] flex-wrap gap-1">
              {app.categories.map((category) => (
                <Tag key={category}>{category}</Tag>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        id: 'updatedAt',
        header: '更新时间',
        width: 180,
        accessor: (app) => (
          <span className="text-muted-foreground">
            {formatProtoTimestamp(app.updatedAt)}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '操作',
        width: 120,
        accessor: (app) => (
          <div className="flex items-center gap-1">
            <Tooltip title="版本管理">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => applicationStore.openVersions(app)}
              >
                <Layers3 className="h-4 w-4" />
              </Button>
            </Tooltip>
            <Tooltip title="编辑应用">
              <Button variant="ghost" size="sm" onClick={() => onEdit(app)}>
                <Edit3 className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ];

    return (
      <DataTable
        columns={columns}
        data={applicationStore.filteredApps}
        loading={applicationStore.appsLoading}
        emptyMessage="暂无应用"
      />
    );
  },
);
